import { createRoot } from "react-dom/client";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import * as Sentry from "@sentry/react";
import "src/assets/css/app.css";
import { AppProviders } from "src/context";
import { AppInitializer } from "src/initializers";
import ProtectedLayout from "src/layouts/ProtectedLayout";
import DealerLayout from "src/layouts/DealerLayout";
import Dashboard from "src/pages/DashboardPage";
import ProgressPage from "src/pages/ProgressPage";
import SignaturePage from "src/pages/ProgressPage/SignaturePage";
import LandingPage from "src/pages/LandingPage";
import TestAuth from "src/pages/TestAuth";
import DocusignTest from "src/pages/DocusignTest";
import RegisterPage from "src/pages/sections/registration";
import LoginPage from "src/pages/LoginPage";
import MagicLinkSentPage from "src/pages/MagicLinkSentPage";
import CitizenshipPage from "src/pages/sections/personal-info/CitizenshipPage";
import MaritalStatusPage from "src/pages/sections/personal-info/MaritalStatusPage";
import DependentsPage from "src/pages/sections/personal-info/DependentsPage";
import BankruptcyPage from "src/pages/sections/expenses/BankruptcyPage";
import ListIncomePage from "src/pages/sections/income/ListIncomePage";
import AccountListPage from "src/pages/sections/financial-accounts/FinancialAccountListPage";
import HomeManufactureDetailsPage from "src/pages/sections/property-home/HomeManufactureDetailsPage";
import HomeDownPaymentPage from "src/pages/sections/property-home/HomeDownPaymentPage";
import HomeUsagePage from "src/pages/sections/property-home/HomeUsagePage";
import HomePlacementPage from "src/pages/sections/property-home/HomePlacementPage";
import LandSituationPage from "src/pages/sections/property-land/LandSituationPage";
import HispanicOrLatinoPage from "src/pages/sections/ethnic-background/HispanicOrLatinoPage";
import RacePage from "src/pages/sections/ethnic-background/RacePage";
import SexPage from "src/pages/sections/ethnic-background/SexPage";
import LoanOverviewPage from "src/pages/sections/loan-overview/LoanOverviewPage";
import ConsentPage from "src/pages/sections/final-steps/ConsentPage";
import SalesPersonPage from "src/pages/sections/final-steps/SalesPersonPage";
import ListAddressesPage from "src/pages/sections/address/ListAddressesPage";
import CoborrowerAddressConfirmationPage from "src/pages/sections/address/CoborrowerAddressConfirmationPage";
import ListEmploymentPage from "src/pages/sections/employment/ListEmploymentPage";
import NotEmployedPage from "src/pages/sections/employment/NotEmployedPage";
import ExpenseListPage from "src/pages/sections/expenses/ExpenseListPage";
import UnauthorizedPage from "src/pages/UnauthorizedPage";
import FullTest from "src/pages/FullTest";
import TestLogout from "src/pages/TestLogout";
import RootRedirectHandler from "./components/RootRedirectHandler";
import Logout from "./pages/Logout";
import SMSSentPage from "src/pages/SMSSentPage";
import NotFound from "src/pages/NotFound";
import InviteCoborrowerPage from "./pages/ProgressPage/InviteCoborrowerPage";
import DownloadSignedDocuments from "./pages/DownloadSignedDocuments";

const replayIntegrationConfig = {
  mask: ["*:not([data-replay-safe])"],
  unmask: ["[data-replay-safe]"],
  maskAllInputs: false,
};

Sentry.init({
  dsn: __SENTRY_DSN__,
  environment: __ENV__,
  release: __SENTRY_RELEASE__,
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.browserProfilingIntegration(),
    Sentry.replayIntegration(replayIntegrationConfig),
  ],
  replaysSessionSampleRate: 0.0,
  replaysOnErrorSampleRate: 1.0,
});

const App = () => {
  const isProduction = __ENV__ === "production";
  return (
    <AppProviders>
      <div className="h-full min-h-screen">
        <Router>
          <AppInitializer>
            <Routes>
              <Route path="/" element={<RootRedirectHandler />} />
              <Route path={":dealerName"} element={<DealerLayout />}>
                <Route path="logout" element={<Logout />} />
                {!isProduction && (
                  <Route path="dev">
                    <Route index element={<LandingPage />} />
                    <Route path="auth" element={<TestAuth />} />
                    <Route path="logout" element={<TestLogout />} />
                  </Route>
                )}
                <Route index element={<LoginPage />} />
                <Route path="login">
                  <Route index element={<LoginPage />} />
                  <Route path="register" element={<LoginPage />} />
                  <Route path="email-sent" element={<MagicLinkSentPage />} />
                  <Route path="sms-sent" element={<SMSSentPage />} />
                </Route>
                <Route path="unauthorized" element={<UnauthorizedPage />} />
                <Route path="dashboard" element={<ProtectedLayout />}>
                  <Route index element={<Dashboard />} />
                </Route>
                <Route path="register" element={<ProtectedLayout />}>
                  <Route index element={<RegisterPage />} />
                  <Route
                    path=":application_id/coborrower/:borrower_id"
                    element={<RegisterPage />}
                  />
                </Route>
                <Route path="docusign-test" element={<DocusignTest />} />
                <Route
                  path="application/:application_id/"
                  element={<ProtectedLayout />}
                >
                  <Route path="progress" element={<ProgressPage />} />
                  <Route path="signature" element={<SignaturePage />} />
                  <Route
                    path="coborrower-invite"
                    element={<InviteCoborrowerPage />}
                  />
                </Route>
                <Route
                  path="application/:application_id/borrower/:borrower_id"
                  element={<ProtectedLayout />}
                >
                  <Route path="personal-info">
                    <Route index element={<RegisterPage />} />
                    <Route path="citizenship" element={<CitizenshipPage />} />
                    <Route
                      path="marital-status"
                      element={<MaritalStatusPage />}
                    />
                    <Route path="dependents" element={<DependentsPage />} />
                  </Route>
                  <Route path="address">
                    <Route index element={<ListAddressesPage />} />
                    <Route
                      path="coborrower-confirmation"
                      element={<CoborrowerAddressConfirmationPage />}
                    />
                  </Route>
                  <Route path="employment" element={<ListEmploymentPage />} />
                  <Route path="no-employment" element={<NotEmployedPage />} />
                  <Route path="income" element={<ListIncomePage />} />
                  <Route
                    path="financial-accounts"
                    element={<AccountListPage />}
                  />
                  <Route path="expenses">
                    <Route index element={<ExpenseListPage />} />
                    <Route path="bankruptcy" element={<BankruptcyPage />} />
                  </Route>
                  <Route path="home">
                    <Route index element={<HomeUsagePage />} />
                    <Route path="placement" element={<HomePlacementPage />} />
                    <Route
                      path="manufacture-detail"
                      element={<HomeManufactureDetailsPage />}
                    />
                    <Route
                      path="down-payment"
                      element={<HomeDownPaymentPage />}
                    />
                  </Route>
                  <Route path="land" element={<LandSituationPage />} />
                  <Route path="ethnicity">
                    <Route index element={<HispanicOrLatinoPage />} />
                    <Route path="race" element={<RacePage />} />
                    <Route path="sex" element={<SexPage />} />
                  </Route>
                  <Route path="overview" element={<LoanOverviewPage />} />
                  <Route
                    path="consent"
                    element={<Navigate to="final-steps/consent" replace />}
                  />
                  <Route path="final-steps">
                    <Route path="consent" element={<ConsentPage />} />
                    <Route path="sales-person" element={<SalesPersonPage />} />
                  </Route>
                </Route>
                {/* Route for testing purpose only */}
                <Route path="application/:application_id">
                  <Route path="full" element={<FullTest />} />
                </Route>
              </Route>
              <Route path="/not-found" element={<NotFound />} />
              <Route
                path="download-signed-documents/:application_id/:document_id"
                element={<DownloadSignedDocuments />}
              />
            </Routes>
          </AppInitializer>
        </Router>
      </div>
    </AppProviders>
  );
};

const mainElement = document.getElementById("main");
if (!mainElement) {
  throw new Error("Root element #main not found");
}
const root = createRoot(mainElement);
root.render(<App />);
